import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, ArrowLeft, Search, Shield, GraduationCap, Home, Award,
  Trash2, AlertTriangle,
} from 'lucide-react';

const ROLE_META = {
  admin: { ar: 'مسؤول', en: 'Admin', icon: Shield, color: 'bg-rose-100 text-rose-700 border-rose-300' },
  teacher: { ar: 'معلم', en: 'Teacher', icon: GraduationCap, color: 'bg-primary/10 text-primary border-primary/30' },
  parent: { ar: 'ولي أمر', en: 'Parent', icon: Home, color: 'bg-accent/15 text-accent-foreground border-accent/30' },
  student: { ar: 'طالب', en: 'Student', icon: Award, color: 'bg-purple-100 text-purple-700 border-purple-300' },
};

export default function AdminUsersPage() {
  const { lang } = useApp();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editRoleFor, setEditRoleFor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const reload = useCallback(() => {
    api.get('/admin/users').then(r => setUsers(r.data));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (q && !(u.email.includes(q) || u.full_name.includes(q))) return false;
    return true;
  });

  const counts = {
    all: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    teacher: users.filter(u => u.role === 'teacher').length,
    parent: users.filter(u => u.role === 'parent').length,
    student: users.filter(u => u.role === 'student').length,
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-users-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/admin"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'لوحة الإدارة' : 'Admin'}</Link>
      </Button>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-rose-100 grid place-items-center"><Users className="w-6 h-6 text-rose-600" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'إدارة المستخدمين' : 'User management'}</h1>
          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'تغيير الأدوار، الحذف، البحث، وفلترة المستخدمين' : 'Change roles, delete, search & filter users.'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === 'ar' ? 'بحث (بريد، اسم)...' : 'Search (email, name)...'} className="ps-10" data-testid="users-search" />
        </div>
        {['all', 'admin', 'teacher', 'parent', 'student'].map(r => (
          <Button key={r} size="sm" variant={roleFilter === r ? 'default' : 'outline'} onClick={() => setRoleFilter(r)} data-testid={`users-filter-${r}`} className={roleFilter === r ? 'bg-rose-600 hover:bg-rose-700' : ''}>
            {r === 'all' ? (lang === 'ar' ? 'الكل' : 'All') : (lang === 'ar' ? ROLE_META[r]?.ar : ROLE_META[r]?.en)}
            <Badge variant="secondary" className="ms-2 text-[10px]">{counts[r]}</Badge>
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">{lang === 'ar' ? 'لا نتائج' : 'No results'}</p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map(u => {
                const meta = ROLE_META[u.role] || ROLE_META.student;
                const Icon = meta.icon;
                return (
                  <li key={u.id} className="flex items-center justify-between p-4 gap-3 flex-wrap hover:bg-secondary/30 transition-colors" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center font-black text-sm shrink-0">
                        {u.full_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm line-clamp-1">{u.full_name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{u.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] border ${meta.color}`} variant="outline">
                        <Icon className="w-3 h-3 me-1" />
                        {lang === 'ar' ? meta.ar : meta.en}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => setEditRoleFor(u)} data-testid={`edit-role-${u.id}`}>{lang === 'ar' ? 'تغيير الدور' : 'Change role'}</Button>
                      <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(u)} className="text-destructive hover:bg-destructive/10" data-testid={`delete-user-${u.id}`} aria-label="delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Edit role dialog */}
      <Dialog open={!!editRoleFor} onOpenChange={(v) => !v && setEditRoleFor(null)}>
        <DialogContent data-testid="edit-role-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'ar' ? 'تغيير دور المستخدم' : 'Change user role'}</DialogTitle>
            <DialogDescription>{editRoleFor?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline me-1" />{lang === 'ar' ? 'تغيير الدور يغيّر صلاحيات المستخدم فوراً.' : 'Changing the role updates user permissions immediately.'}</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(ROLE_META).map(r => {
                const meta = ROLE_META[r];
                const Icon = meta.icon;
                const isCurrent = editRoleFor?.role === r;
                return (
                  <button
                    key={r}
                    disabled={isCurrent}
                    onClick={async () => {
                      try {
                        await api.post(`/admin/users/${editRoleFor.id}/role`, { role: r });
                        toast.success(lang === 'ar' ? 'تم تحديث الدور' : 'Role updated');
                        setEditRoleFor(null);
                        reload();
                      } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
                    }}
                    data-testid={`role-pick-${r}`}
                    className={`border-2 rounded-xl p-3 transition-all text-start ${isCurrent ? 'bg-secondary/60 cursor-not-allowed opacity-60' : 'border-border hover:border-rose-300 hover:bg-rose-50/30'}`}
                  >
                    <Icon className="w-5 h-5 text-rose-600 mb-1" />
                    <div className="font-bold text-sm">{lang === 'ar' ? meta.ar : meta.en}</div>
                    {isCurrent && <div className="text-[10px] text-muted-foreground mt-0.5">{lang === 'ar' ? 'الدور الحالي' : 'Current role'}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2"><AlertTriangle className="w-5 h-5" />{lang === 'ar' ? 'تأكيد الحذف' : 'Confirm delete'}</DialogTitle>
            <DialogDescription>{confirmDelete?.email}</DialogDescription>
          </DialogHeader>
          <p className="text-sm">{lang === 'ar' ? 'سيتم حذف هذا المستخدم نهائياً. لا يمكن التراجع.' : 'This user will be permanently deleted. This action cannot be undone.'}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant="destructive" onClick={async () => {
              try {
                await api.delete(`/admin/users/${confirmDelete.id}`);
                toast.success(lang === 'ar' ? 'تم الحذف' : 'Deleted');
                setConfirmDelete(null);
                reload();
              } catch (e) { toast.error(e.response?.data?.detail || 'error'); }
            }} className="flex-1" data-testid="confirm-delete-user">
              <Trash2 className="w-4 h-4 me-1" />{lang === 'ar' ? 'حذف نهائي' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
